import { getCalculationInputTableCellDisplayValue } from "#batteries-included-components/Tabs/EstimationMethod/helpers";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useQuery } from "@tanstack/react-query";
import {
  DataTable,
  DataTablePanel,
  HeaderType,
  Link,
} from "@validereinc/common-components";
import {
  EstimationMethodAdapter,
  EstimationMethodRunType,
  Resources,
  SortDirection,
  type AssetTypeType,
} from "@validereinc/domain";
import { DateFormats, monthFormatter } from "@validereinc/utilities";
import parse from "date-fns/parse";
import parseISO from "date-fns/parseISO";
import React from "react";

const EstimationMethodRecordsTab = ({
  methodId,
  assetId,
  assetType,
  onClickRow,
}: {
  methodId: string;
  assetType: AssetTypeType;
  assetId: string;
  onClickRow: (methodId: string, entityId: string, yearMonth: string) => void;
}) => {
  const methodDetailsQuery = useQuery({
    queryKey: [Resources.ESTIMATION_METHOD, methodId, { assetType }],
    queryFn: () => {
      return EstimationMethodAdapter.getOne({
        id: methodId,
        meta: {
          entityType: assetType,
        },
      });
    },
    select: (resp) => resp.data,
    enabled: !!methodId,
  });

  const methodDetail = methodDetailsQuery?.data;
  const [tableConfigStorageKey, tableStateStorageKey] = useStorageKey(
    ["table-configuration", "table-state"],
    `${assetType}-estimation-methods-detail-${
      methodDetail?.analytics_calculator_id ?? assetId
    }`
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "year_month",
      sortDirection: SortDirection.ASCENDING,
    },
    {
      methodId: methodDetail?.id ?? "",
      entityType: assetType,
    },
    tableStateStorageKey
  );
  const estMethodRunListQueryParams: Parameters<
    typeof EstimationMethodAdapter.run.getList
  >[0] = {
    filters: {
      methodId: methodDetail?.id ?? "",
      entityType: assetType,
    },
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
  };
  const estMethodRunListQuery = useQuery({
    queryKey: [
      Resources.ESTIMATION_METHOD,
      "runs",
      estMethodRunListQueryParams,
    ],
    queryFn: () =>
      EstimationMethodAdapter.run.getList(estMethodRunListQueryParams),
    enabled: Boolean(
      methodDetail?.id && assetId && assetType && tableState.page
    ),
  });
  const { getPrecisionByType, getTypeName, getUnitName } =
    useMeasurementTypes();

  const extractInputHeadersFromRun = (
    runData?: EstimationMethodRunType
  ): Array<HeaderType<EstimationMethodRunType>> => {
    if (!runData?.input?.calculation_parameters?.length) {
      return [];
    }

    return [
      {
        key: "inputs",
        label: "Inputs",
        headers: runData.input.calculation_parameters.map((input) => {
          const unit = getUnitName(input.measurement_unit);

          return {
            label: `${input.display_name}${unit ? ` [${unit}]` : ""}`,
            key: input.id,
            tooltip: input.description,
            alignment: "right",
            isSortable: false,
            renderComponent: ({ item }: { item: EstimationMethodRunType }) => {
              // find the right input result
              const inputResult = item?.input?.calculation_parameters?.find(
                (inputCandidate) => inputCandidate.id === input.id
              );

              const { measurement_unit, ...inputResultWithoutUnit } =
                inputResult ?? {};

              return getCalculationInputTableCellDisplayValue(
                // @ts-expect-error TODO: missed typing
                inputResultWithoutUnit,
                "right"
              );
            },
          };
        }),
      },
    ];
  };
  const extractOutputHeadersFromRun = (
    runData?: EstimationMethodRunType
  ): Array<HeaderType<EstimationMethodRunType>> => {
    if (!runData?.output?.outputs?.length) {
      return [];
    }

    return [
      {
        key: "results",
        label: "Results",
        headers: runData.output.outputs.map((output) => {
          const unit = getUnitName(output.measurement_unit);

          return {
            label: `${getTypeName(output.measurement_type)}${unit ? ` [${unit}]` : ""}`,
            key: output.measurement_type,
            tooltip: output.description,
            alignment: "right",
            isSortable: false,
            renderComponent: ({ item }) => {
              // find the right output result
              const outputResult = item?.output?.outputs?.find(
                (outputCandidate) =>
                  outputCandidate.measurement_type === output.measurement_type
              );
              const precision = getPrecisionByType(output.measurement_type);

              return (
                <DataTable.DataRow.NumberCell
                  value={outputResult?.measurement_value}
                  formattingOpts={
                    precision ? { fractionDigits: precision } : {}
                  }
                />
              );
            },
          };
        }),
      },
    ];
  };

  const tableHeaders: Array<HeaderType<EstimationMethodRunType>> = [
    {
      label: "Time Period",
      key: "year_month",
      isSortable: true,
      renderComponent: ({
        item: { year_month: yearMonth, calculation_start: calculationStart },
      }) => (
        <Link
          label={
            yearMonth && calculationStart
              ? monthFormatter(
                  parse(
                    yearMonth,
                    DateFormats.YEAR_MONTH,
                    parseISO(calculationStart)
                  )
                )
              : "-"
          }
          onClick={() =>
            onClickRow(methodDetail?.id ?? methodId, assetId, yearMonth)
          }
        />
      ),
    },
    ...extractInputHeadersFromRun(estMethodRunListQuery.data?.data?.[0]),
    ...extractOutputHeadersFromRun(estMethodRunListQuery.data?.data?.[0]),
  ];

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{
        title: "Results",
      }}
      dataTableProps={{
        ...tableState,
        items: estMethodRunListQuery.data?.data ?? [],
        getItemId: (item) => item.id,
        isLoading: estMethodRunListQuery.isLoading,
        isBusy: estMethodRunListQuery.isFetching,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: estMethodRunListQuery.data?.total_entries,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
        headers: tableHeaders,
      }}
    />
  );
};

export default EstimationMethodRecordsTab;
